import { useState, type FormEvent } from 'react';
import { ApiError, toUserMessage } from '@platform/api-client';

import { Alert, EmailSplitField } from '@/shared/ui';
import { PhoneVerifyField } from './PhoneVerifyField';
import {
  useResendVerificationAnonymous,
  useSignupRecoveryEmail,
  useSignupRecoveryLookup,
} from '../api/use-auth.mutation';
import { signupRecoveryEmailSchema } from '../schemas/auth.schemas';

interface SignupEmailRecoveryProps {
  /** 로그인 폼에 친 값(서버 전송 형태 — trim·소문자). */
  loginId: string;
  orgSlug?: string;
}

type Step =
  | { kind: 'verify' }
  | { kind: 'confirm'; email: string; editing: boolean }
  | { kind: 'done'; email: string };

/** OTP 증표가 없거나 만료(10분)·소비됨 — 휴대폰 인증부터 다시. */
const isPhoneProofGone = (err: unknown) =>
  err instanceof ApiError && err.code === 'AUTH_PHONE_NOT_VERIFIED';

/**
 * 인증한 번호가 이 계정의 가입 번호와 다름. 이 화면은 로그인(아이디·비밀번호)이
 * 맞아야 열리므로 사실상 번호가 틀린 경우다 — 그 자리에서 번호를 고쳐 다시 인증하게 한다.
 */
const isSignupInfoMismatch = (err: unknown) =>
  err instanceof ApiError && err.code === 'AUTH_SIGNUP_INFO_MISMATCH';

const PHONE_MISMATCH_NOTICE =
  '가입 시 인증한 휴대폰 번호와 일치하지 않습니다.\n번호를 확인한 후 다시 인증해 주세요.';

/**
 * 가입정보 확인 — 가입 이메일을 잘못 입력해 인증 메일을 못 받는 사용자의 복구 경로.
 *
 * 1) 가입 때 인증한 휴대폰으로 OTP → 2) 가입된 이메일 확인 → 3) 틀렸으면 고쳐서 재발송.
 * 본인 확인은 휴대폰 OTP 하나로 한다. 서버는 조회 때 OTP 증표를 확인만 하고
 * 이메일 재설정 때 소비하므로, 2→3 사이는 증표 유효시간(10분) 안에 끝내야 한다.
 */
export function SignupEmailRecovery({
  loginId,
  orgSlug,
}: SignupEmailRecoveryProps) {
  const [step, setStep] = useState<Step>({ kind: 'verify' });
  const [phone, setPhone] = useState('');
  const [verified, setVerified] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [fieldError, setFieldError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const lookup = useSignupRecoveryLookup();
  const changeEmail = useSignupRecoveryEmail();
  const resend = useResendVerificationAnonymous();

  /**
   * 휴대폰 인증 칸을 처음 상태로 되돌린다(입력한 번호는 남겨 둠).
   * PhoneVerifyField 는 인증 완료 후 번호 칸을 잠그고 발송·코드 상태를 내부에 들고 있어,
   * key 를 바꿔 다시 마운트해야 번호를 고쳐 바로 재인증할 수 있다.
   */
  const [phoneFieldKey, setPhoneFieldKey] = useState(0);
  const reopenPhone = (message?: string) => {
    setVerified(false);
    setPhoneFieldKey((k) => k + 1);
    setNotice(message);
  };

  const account = { loginId, ...(orgSlug ? { orgSlug } : {}) };

  const restart = (message: string) => {
    setStep({ kind: 'verify' });
    reopenPhone(message);
  };

  const handleLookup = () => {
    setNotice(undefined);
    lookup.mutate(
      { ...account, phone },
      {
        onSuccess: ({ email }) =>
          setStep({ kind: 'confirm', email, editing: false }),
        onError: (err) => {
          if (isSignupInfoMismatch(err)) {
            lookup.reset(); // 원문 오류 대신 아래 안내로 대체
            reopenPhone(PHONE_MISMATCH_NOTICE);
          } else if (isPhoneProofGone(err)) {
            reopenPhone();
          }
        },
      },
    );
  };

  const handleResendSame = (email: string) => {
    resend.mutate(email, {
      onSuccess: () => setStep({ kind: 'done', email }),
    });
  };

  const handleChangeEmail = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = signupRecoveryEmailSchema.safeParse({ newEmail });
    if (!parsed.success) {
      setFieldError(parsed.error.flatten().fieldErrors.newEmail?.[0]);
      return;
    }
    setFieldError(undefined);
    changeEmail.mutate(
      { ...account, phone, newEmail: parsed.data.newEmail },
      {
        onSuccess: () =>
          setStep({ kind: 'done', email: parsed.data.newEmail.trim() }),
        onError: (err) => {
          if (isPhoneProofGone(err))
            restart('인증 유효 시간이 만료되었습니다.\n휴대폰 인증을 다시 진행해 주세요.');
        },
      },
    );
  };

  if (step.kind === 'done') {
    return (
      <Alert tone="success">
        <b className="break-all">{step.email}</b> 주소로 인증 메일을
        발송했습니다.
        <br />
        메일의 인증 링크를 클릭한 후 다시 로그인해 주세요.
      </Alert>
    );
  }

  if (step.kind === 'confirm') {
    return (
      <div className="space-y-3">
        <div className="rounded-lg bg-neutral-50 px-4 py-3 dark:bg-neutral-800/50">
          <p className="text-xs text-adobe-muted dark:text-neutral-400">
            가입된 이메일
          </p>
          <p className="mt-0.5 break-all font-mono text-sm font-semibold text-adobe-dark dark:text-neutral-100">
            {step.email}
          </p>
        </div>

        {step.editing ? (
          <form onSubmit={handleChangeEmail} className="space-y-3" noValidate>
            <EmailSplitField
              id="signupRecoveryNewEmail"
              label="변경할 이메일"
              onChange={(v) => {
                setNewEmail(v);
                if (fieldError) setFieldError(undefined);
                if (changeEmail.error) changeEmail.reset();
              }}
              error={fieldError}
            />
            {changeEmail.error && (
              <Alert tone="error">{toUserMessage(changeEmail.error)}</Alert>
            )}
            <button
              type="submit"
              disabled={changeEmail.isPending}
              className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
            >
              {changeEmail.isPending ? '변경 중…' : '이메일 변경 및 인증 메일 발송'}
            </button>
          </form>
        ) : (
          <>
            {resend.error && (
              <Alert tone="error">{toUserMessage(resend.error)}</Alert>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleResendSame(step.email)}
                disabled={resend.isPending}
                className="flex-1 rounded-lg border border-brand-500 py-2.5 text-sm font-semibold text-brand-600 transition-colors hover:bg-brand-50 disabled:opacity-50 dark:hover:bg-brand-500/10"
              >
                {resend.isPending ? '발송 중…' : '현재 이메일로 재발송'}
              </button>
              <button
                type="button"
                onClick={() => setStep({ ...step, editing: true })}
                className="flex-1 rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600"
              >
                이메일 수정
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PhoneVerifyField
        key={phoneFieldKey}
        phone={phone}
        onPhoneChange={(value) => {
          setPhone(value);
          setVerified(false);
          if (lookup.error) lookup.reset();
        }}
        verified={verified}
        onVerified={() => {
          setVerified(true);
          setNotice(undefined);
        }}
      />
      {notice && <Alert tone="warning">{notice}</Alert>}
      {lookup.error && <Alert tone="error">{toUserMessage(lookup.error)}</Alert>}
      <button
        type="button"
        onClick={handleLookup}
        disabled={!verified || lookup.isPending}
        className="w-full rounded-lg bg-brand-500 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-600 disabled:opacity-50"
      >
        {lookup.isPending ? '확인 중…' : '가입 정보 확인'}
      </button>
    </div>
  );
}
